import { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row, Space, Table, Form, Input, Switch, notification, Select } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { 
  searchActivations, 
  addActivation, 
  updateActivation, 
  deleteActivation, 
  deleteMultipleActivations 
} from "../../services/activationService";
import { useSelector } from "react-redux";

export const ActivationPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchForm] = Form.useForm();
    const [currentSearchValues, setCurrentSearchValues] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { token } = useSelector((state: any) => state.auth);

    const loadData = useCallback(async (searchValues = {}) => {
        setLoading(true);
        try {
            // Use current search values if no new search values are provided
            const valuesToUse = Object.keys(searchValues).length > 0 ? searchValues : currentSearchValues;
            
            const response = await searchActivations({
                pageIndex,
                pageSize,
                ...valuesToUse
            }, token);
            
            if (response.status) {
                setData(response.data.items);
                setTotal(response.data.totalItems);
            }
        } catch (error) {
            console.error("Error loading activations:", error);
            notification.error({
                message: "Error",
                description: "Failed to load activations"
            });
        }
        setLoading(false);
    }, [currentSearchValues, pageIndex, pageSize, token]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const columns = [
        {
            title: "App ID",
            dataIndex: "appId",
            key: "appId",
        },
        {
            title: "Version Code",
            dataIndex: "versionCode",
            key: "versionCode",
        },
        {
            title: "Version Name",
            dataIndex: "versionName",
            key: "versionName",
        },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (
                <Switch checked={isActive} disabled />
            ),
        },
        // {
        //     title: "Show",
        //     dataIndex: "isShow",
        //     key: "isShow",
        //     render: (isShow: boolean) => (
        //         <Switch checked={isShow} disabled />
        //     ),
        // },
        {
            title: "Auth",
            dataIndex: "isAuth",
            key: "isAuth",
            render: (isAuth: boolean) => (
                <Switch checked={isAuth} disabled />
            ),
        },
        {
            title: "Payment",
            dataIndex: "isPayment",
            key: "isPayment",
            render: (isPayment: boolean) => (
                <Switch checked={isPayment} disabled />
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    />
                </Space>
            ),
        },
    ];

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (record) => {
        Modal.confirm({
            title: "Are you sure you want to delete this activation?",
            onOk: async () => {
                try {
                    await deleteActivation(record.appId, record.versionCode, token);
                    
                    // Update client-side state instead of reloading data
                    setData(prevData => 
                        prevData.filter(item => 
                            !(item.appId === record.appId && item.versionCode === record.versionCode)
                        )
                    );
                    // Update total count
                    setTotal(prevTotal => prevTotal - 1);
                    
                    notification.success({
                        message: "Success",
                        description: "Activation deleted successfully"
                    });
                } catch (error) {
                    notification.error({
                        message: "Error",
                        description: "Failed to delete activation"
                    });
                }
            },
        });
    };

    const handleDeleteMultiple = () => {
        if (selectedRowKeys.length === 0) {
            notification.warning({
                message: "Warning",
                description: "Please select at least one record to delete"
            });
            return;
        }

        Modal.confirm({
            title: `Are you sure you want to delete ${selectedRowKeys.length} selected activation(s)?`,
            content: "This action cannot be undone.",
            onOk: async () => {
                setDeleteLoading(true);
                try {
                    const response = await deleteMultipleActivations(selectedRowKeys, token);
                    
                    if (response.status) {
                        // Update client-side state
                        setData(prevData => 
                            prevData.filter(item => !selectedRowKeys.includes(item._id))
                        );
                        
                        // Update total count
                        setTotal(prevTotal => prevTotal - selectedRowKeys.length);
                        
                        notification.success({
                            message: "Success",
                            description: `${response.deletedCount} activation(s) deleted successfully`
                        });
                        
                        // Clear selections
                        setSelectedRowKeys([]);
                    } else {
                        notification.error({
                            message: "Error",
                            description: response.message || "Failed to delete activations"
                        });
                    }
                } catch (error) {
                    console.error("Error deleting activations:", error);
                    notification.error({
                        message: "Error",
                        description: "Failed to delete activations"
                    });
                } finally {
                    setDeleteLoading(false);
                }
            }
        });
    };

    const handleSubmit = async (values) => {
        try {
            if (editingRecord) {
                await updateActivation(
                    editingRecord.appId,
                    editingRecord.versionCode,
                    values,
                    token
                );
            } else {
                await addActivation(values, token);
            }
            notification.success({
                message: "Success",
                description: `Activation ${editingRecord ? "updated" : "added"} successfully`
            });
            setIsModalVisible(false);
            form.resetFields();
            setEditingRecord(null);
            loadData();
        } catch (error) {
            notification.error({
                message: "Error",
                description: `Failed to ${editingRecord ? "update" : "add"} activation`
            });
        }
    };

    const handleSearch = (values) => {
        // Store the search values for pagination
        setCurrentSearchValues(values);
        setPageIndex(0); // Reset to first page when searching
        loadData(values);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: string[]) => setSelectedRowKeys(keys),
    };

    return (
        <Card title="Activations">
            <Form
                form={searchForm}
                layout="vertical"
                onFinish={handleSearch}
                style={{ marginBottom: 16 }}
            >
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="appId" label="App ID">
                            <Input placeholder="Search by App ID" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="versionCode" label="Version Code">
                            <Input type="number" placeholder="Search by Version Code" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="isActive" label="Status">
                            <Select
                                allowClear
                                placeholder="Select Status"
                                options={[
                                    { value: true, label: 'Active' },
                                    { value: false, label: 'Inactive' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label=" " colon={false}>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                    Search
                                </Button>
                                <Button onClick={() => {
                                    searchForm.resetFields();
                                    loadData();
                                }}>
                                    Reset
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Row justify="space-between" style={{ marginBottom: 16 }}>
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteMultiple}
                    disabled={selectedRowKeys.length === 0}
                    loading={deleteLoading}
                >
                    Delete Selected ({selectedRowKeys.length})
                </Button>
                
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingRecord(null);
                        form.resetFields();
                        setIsModalVisible(true);
                    }}
                >
                    Add New
                </Button>
            </Row>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="_id"
                pagination={{
                    total,
                    pageSize,
                    current: pageIndex + 1,
                    onChange: (page, pageSize) => {
                        setPageIndex(page - 1);
                        setPageSize(pageSize);
                    },
                }}
            />

            <Modal
                title={
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        {editingRecord ? "Edit Activation" : "Add New Activation"}
                    </div>
                }
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={700}
                centered
                bodyStyle={{ padding: '24px' }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                >
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="appId"
                                label="App ID"
                                rules={[{ required: true, message: 'Please enter App ID' }]}
                            >
                                <Input 
                                    disabled={!!editingRecord} 
                                    placeholder="Enter App ID"
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="versionCode"
                                label="Version Code"
                                rules={[{ required: true, message: 'Please enter Version Code' }]}
                            >
                                <Input 
                                    type="number" 
                                    disabled={!!editingRecord} 
                                    placeholder="Enter Version Code"
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="versionName"
                        label="Version Name"
                        rules={[{ required: true, message: 'Please enter Version Name' }]}
                    >
                        <Input 
                            placeholder="Enter Version Name (e.g. 1.0.0)"
                            size="large"
                        />
                    </Form.Item>

                    <Card 
                        title="Activation Settings" 
                        size="small" 
                        bordered={true} 
                        style={{ marginBottom: '20px', backgroundColor: '#fafafa' }}
                    >
                        <Row gutter={48}>
                            <Col span={12}>
                                <Form.Item
                                    name="isActive"
                                    label="Active"
                                    valuePropName="checked"
                                    tooltip="Enable/disable this activation"
                                >
                                    <Switch 
                                        checkedChildren="Enabled" 
                                        unCheckedChildren="Disabled"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="isShow"
                                    label="Show"
                                    valuePropName="checked"
                                    tooltip="Control visibility in client"
                                >
                                    <Switch 
                                        checkedChildren="Yes" 
                                        unCheckedChildren="No"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="isAuth"
                                    label="Authentication"
                                    valuePropName="checked"
                                    tooltip="Enable/disable authentication requirement"
                                >
                                    <Switch 
                                        checkedChildren="Required" 
                                        unCheckedChildren="Optional"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="isPayment"
                                    label="Payment"
                                    valuePropName="checked"
                                    tooltip="Enable/disable payment requirement"
                                >
                                    <Switch 
                                        checkedChildren="Enabled" 
                                        unCheckedChildren="Disabled"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                    
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', textAlign: 'right' }}>
                        <Space>
                            <Button 
                                onClick={() => setIsModalVisible(false)}
                                size="large"
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                size="large"
                                icon={editingRecord ? <EditOutlined /> : <PlusOutlined />}
                            >
                                {editingRecord ? "Update Activation" : "Create Activation"}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </Card>
    );
};
